import React from "react";
import { CheckBox, FileDownloader } from "src/Components";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import TableTemplate, {
  ITableUI,
} from "src/Tables/TableTemplate/tableTemplate";
import {
  getPortfolioGroupAccountsDropdown,
  getPortfoSegregationSummaryTable,
  getPortfoSegregationSummaryExcel,
  getPortfoSegregationTable,
  getPortfoSegregationExcel,
  getPortfoSegregationDetailsTable,
  getDeltaHedgeSegregationDetailsTable,
  getCoveredCallAllocationTable,
  getCoveredCallAllocationExcel,
  getCoveredCallPortfolioTable,
  getConversionAllocationTable,
  getConversionAllocationExcel,
  getConversionPortfolioTable,
  getNakedOptionPortfolioExcel,
  getNakedOptionPortfolioTable,
} from "src/Requests/portfolioGroups";
import type {
  IPortfoSegregationSearchParams,
  IPortfoSegregationSummarySearchParams,
  IPortfoSegregationSummaryTableReport,
  IPortfoSegregationSummaryTableResponse,
  IPortfoSegregationTableReport,
  IPortfoSegregationDetailsSearchParams,
  IPortfoSegregationDetailsTableReport,
  IPortfoGroupDetailedSearchParams,
  IStrategyPortfolioSearchParams,
  ICoveredCallSummaryTableReport,
  ICoveredCallAllocationTableReport,
  ICoveredCallPortfolioTableReport,
  IConversionSummaryTableReport,
  IConversionAllocationTableReport,
  IConversionPortfolioTableReport,
  INakedOptionPortfolioSearchParams,
  INakedOptionPortfolioTableReport,
} from "src/Interfaces/portfolioGroups";
import UsersDropdown from "src/Components/Dropdown/Users/UsersDropdown";
import GroupTableUI from "../DetailedPortfolio/GroupTableUI";

type StrategyTableType = "coveredCall" | "conversion" | "nakedOption" | null;

const portfoSegregationSummaryTablePaneClassName =
  "flex flex-col shrink-0 basis-[40%] min-w-0";
const assetStrategyTablePaneClassName = "flex flex-col flex-1 min-w-0";

const getBasketGroupHeaderStyle = (
  groupName: string,
): React.CSSProperties | undefined =>
  groupName.includes("خرید")
    ? { backgroundColor: "#ebf9ee" }
    : groupName.includes("فروش")
      ? { backgroundColor: "#ffe7e6" }
      : undefined;

const AssetType = {
  DeltaHedge: 14,
  CoveredCall: 15,
  Conversion: 16,
  LongCall: 17,
  ShortCall: 18,
  LongPut: 19,
  ShortPut: 20,
} as const;

const NakedOptionAssetTypes = new Set<number>([
  AssetType.LongCall,
  AssetType.ShortCall,
  AssetType.LongPut,
  AssetType.ShortPut,
]);

interface ISummaryTableUIProps {
  reports: IPortfoSegregationSummaryTableResponse;
  setPortfoGroupName: (name: string | null) => void;
  onSummaryRowClick: (row: IPortfoSegregationSummaryTableReport) => void;
  activeRowId?: number;
  prevRowRef: React.MutableRefObject<number | null>;
}

const SummaryTableUI: React.FC<
  Omit<ITableUI<IPortfoSegregationSummaryTableReport>, "reports"> &
    ISummaryTableUIProps
> = ({
  reports,
  onAction,
  sortingType,
  setSortingType,
  setPortfoGroupName,
  onSummaryRowClick,
  activeRowId,
  prevRowRef,
}) => {
  React.useEffect(() => {
    setPortfoGroupName(reports.portfoGroupName ?? null);
  }, [reports, setPortfoGroupName]);

  return (
    <CommonTableUI
      reports={reports}
      onAction={onAction}
      sortingType={sortingType}
      setSortingType={setSortingType}
      onTableRowClick={onSummaryRowClick}
      isRowClickable={(row) => row.hasDetail}
      activeRowId={activeRowId}
      tableBoxClassName="!max-h-none pb-1"
      ref={prevRowRef}
    />
  );
};

const AssignmentDetails = () => {
  const prevRowRef = React.useRef<number | null>(null);
  const prevSegregationRowRef = React.useRef<number | null>(null);
  const [summarySearchParams, setSummarySearchParams] =
    React.useState<IPortfoSegregationSummarySearchParams>({
      accountId: null,
    });
  const [showZeroValueAssetTypes, setShowZeroValueAssetTypes] =
    React.useState(false);
  const [segregationSearchParams, setSegregationSearchParams] =
    React.useState<IPortfoSegregationSearchParams | null>(null);
  const [detailsSearchParams, setDetailsSearchParams] =
    React.useState<IPortfoSegregationDetailsSearchParams | null>(null);
  const [selectedSummaryRow, setSelectedSummaryRow] =
    React.useState<IPortfoSegregationSummaryTableReport | null>(null);
  const [selectedSegregationRow, setSelectedSegregationRow] =
    React.useState<IPortfoSegregationTableReport | null>(null);
  const [strategyTableType, setStrategyTableType] =
    React.useState<StrategyTableType>(null);
  const [strategyPortfolioSearchParams, setStrategyPortfolioSearchParams] =
    React.useState<IStrategyPortfolioSearchParams | null>(null);
  const [nakedOptionSearchParams, setNakedOptionSearchParams] =
    React.useState<INakedOptionPortfolioSearchParams | null>(null);
  const [selectedStrategyRowId, setSelectedStrategyRowId] = React.useState<
    number | undefined
  >(undefined);
  const [selectedStrategyBaseAsset, setSelectedStrategyBaseAsset] =
    React.useState<string | null>(null);
  const [portfoGroupName, setPortfoGroupName] = React.useState<string | null>(
    null,
  );

  const filterChangeHandler = (name: string, value: number | string) => {
    setSummarySearchParams((prev) => ({
      ...prev,
      [name]: value === "" || value == null ? null : Number(value),
    }));
  };

  const summaryTableSearchParams = React.useMemo(
    () => ({
      ...summarySearchParams,
      showNonZeroValues: !showZeroValueAssetTypes,
    }),
    [summarySearchParams, showZeroValueAssetTypes],
  );

  React.useEffect(() => {
    prevRowRef.current = null;
    setSelectedSummaryRow(null);
    setSegregationSearchParams(null);
    prevSegregationRowRef.current = null;
    setSelectedSegregationRow(null);
    setDetailsSearchParams(null);
    setStrategyTableType(null);
    setStrategyPortfolioSearchParams(null);
    setNakedOptionSearchParams(null);
    setSelectedStrategyRowId(undefined);
    setSelectedStrategyBaseAsset(null);
    setPortfoGroupName(null);
  }, [summarySearchParams.accountId]);

  const summaryRowClickHandler = (
    row: IPortfoSegregationSummaryTableReport,
  ) => {
    if (!row.hasDetail) return;
    if (prevRowRef.current === row.id) return;
    prevRowRef.current = row.id;
    setSelectedSummaryRow(row);
    prevSegregationRowRef.current = null;
    setSelectedSegregationRow(null);
    setDetailsSearchParams(null);
    setStrategyPortfolioSearchParams(null);
    setNakedOptionSearchParams(null);
    setSelectedStrategyRowId(undefined);
    setSelectedStrategyBaseAsset(null);

    const selectedStrategyType =
      row.assetType === AssetType.CoveredCall
        ? "coveredCall"
        : row.assetType === AssetType.Conversion
          ? "conversion"
          : NakedOptionAssetTypes.has(row.assetType ?? -1)
            ? "nakedOption"
          : null;
    setStrategyTableType(selectedStrategyType);

    if (selectedStrategyType) {
      setSegregationSearchParams(null);
      if (selectedStrategyType === "nakedOption") {
        setNakedOptionSearchParams({
          accountId: summarySearchParams.accountId,
          assetType: row.assetType,
        });
      }
      return;
    }

    if (row.assetType != null && summarySearchParams.accountId != null) {
      setSegregationSearchParams({
        accountId: summarySearchParams.accountId,
        assetType: row.assetType,
        isProgram: row.isProgram,
      });
    }
  };

  const strategySummaryRowClickHandler = (
    row: ICoveredCallSummaryTableReport | IConversionSummaryTableReport,
  ) => {
    const accountId = row.accountId ?? summarySearchParams.accountId;
    if (accountId == null || !row.baseInstrumentId) return;

    setSelectedStrategyRowId(row.id);
    setSelectedStrategyBaseAsset(row.baseAsset ?? null);
    setStrategyPortfolioSearchParams({
      accountId,
      baseInstrumentId: row.baseInstrumentId,
    });
  };

  const segregationRowClickHandler = (row: IPortfoSegregationTableReport) => {
    if (!row.hasDetail) return;
    if (prevSegregationRowRef.current === row.id) return;
    prevSegregationRowRef.current = row.id;
    setSelectedSegregationRow(row);
    setDetailsSearchParams({
      insId: row.insId,
      accountId: row.accountId,
      assetType: row.assetType,
    });
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center gap-3">
        <div className={portfoSegregationSummaryTablePaneClassName}>
          <div className="flex flex-row justify-between items-center gap-3">
            <div className="w-[220px]">
              <UsersDropdown
                label="اکانت"
                name="accountId"
                value={summarySearchParams.accountId?.toString() ?? ""}
                onChange={filterChangeHandler}
                extraLabel
                getData={getPortfolioGroupAccountsDropdown}
              />
            </div>
            <span className="text-sm font-bold mr-6">
              گروه پرتفو: {portfoGroupName ?? "-"}
            </span>
            <div className="w-36">
              <FileDownloader getFile={() =>
                  getPortfoSegregationSummaryExcel(summaryTableSearchParams)
                } />
            </div>
          </div>
        </div>
      </div>

      <CheckBox
        checked={showZeroValueAssetTypes}
        label="نمایش طبقه های با مقادیر صفر"
        name="showZeroValueAssetTypes"
        onChange={(_, checked) => setShowZeroValueAssetTypes(checked)}
      />

      <div className="flex flex-row justify-start items-start gap-3">
        <div className={portfoSegregationSummaryTablePaneClassName}>
          <TableTemplate<
            IPortfoSegregationSummarySearchParams,
            IPortfoSegregationSummaryTableReport
          >
            getDataTable={getPortfoSegregationSummaryTable}
            searchParameters={summaryTableSearchParams}
            TableUI={(props) => (
              <SummaryTableUI
                {...props}
                setPortfoGroupName={setPortfoGroupName}
                onSummaryRowClick={summaryRowClickHandler}
                activeRowId={selectedSummaryRow?.id}
                prevRowRef={prevRowRef}
              />
            )}
            withSort
            tableWrapperClassname="!mx-0"
            refreshTableTime={30000}
          />
        </div>
        <div className={`${assetStrategyTablePaneClassName} -mt-7`}>
          <div className="min-h-8 flex items-center justify-between mb-2 w-full">
            <div className="text-sm font-bold mr-6">
              طبقه دارایی / استراتژی: {selectedSummaryRow?.displayName ?? "-"}
            </div>
            {segregationSearchParams ? (
              <div className="w-36">
                <FileDownloader getFile={() =>
                    getPortfoSegregationExcel(segregationSearchParams)
                  } />
              </div>
            ) : strategyTableType === "coveredCall" ? (
              <div className="w-36">
                <FileDownloader getFile={() => getCoveredCallAllocationExcel(summarySearchParams)} />
              </div>
            ) : strategyTableType === "conversion" ? (
              <div className="w-36">
                <FileDownloader getFile={() => getConversionAllocationExcel(summarySearchParams)} />
              </div>
            ) : strategyTableType === "nakedOption" && nakedOptionSearchParams ? (
              <div className="w-36">
                <FileDownloader getFile={() =>
                    getNakedOptionPortfolioExcel(nakedOptionSearchParams)
                  } />
              </div>
            ) : null}
          </div>
          {strategyTableType === "coveredCall" ? (
            <>
              <TableTemplate<
                IPortfoGroupDetailedSearchParams,
                ICoveredCallAllocationTableReport
              >
                key="covered-call-allocation"
                getDataTable={getCoveredCallAllocationTable}
                searchParameters={summarySearchParams}
                TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                  <CommonTableUI
                    reports={reports}
                    onAction={onAction}
                    sortingType={sortingType}
                    setSortingType={setSortingType}
                    onTableRowClick={strategySummaryRowClickHandler}
                    activeRowId={selectedStrategyRowId}
                    tableBoxClassName="max-h-[500px] pb-1"
                  />
                )}
                withSort
                tableWrapperClassname="!mx-0"
              />
              {strategyPortfolioSearchParams ? (
                <div className="mt-3 w-full">
                  <div className="text-sm font-bold mr-6 mb-2">
                    پرتفوی کاوردکال: {selectedStrategyBaseAsset ?? "-"}
                  </div>
                  <TableTemplate<
                    IStrategyPortfolioSearchParams,
                    ICoveredCallPortfolioTableReport
                  >
                    getDataTable={getCoveredCallPortfolioTable}
                    searchParameters={strategyPortfolioSearchParams}
                    TableUI={(props) => (
                      <GroupTableUI
                        {...props}
                        tableBoxClassName="pb-1"
                        groupHeaderStyle={getBasketGroupHeaderStyle}
                      />
                    )}
                    withSort
                    tableWrapperClassname="!mx-0"
                  />
                </div>
              ) : null}
            </>
          ) : strategyTableType === "conversion" ? (
            <>
              <TableTemplate<
                IPortfoGroupDetailedSearchParams,
                IConversionAllocationTableReport
              >
                key="conversion-allocation"
                getDataTable={getConversionAllocationTable}
                searchParameters={summarySearchParams}
                TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                  <CommonTableUI
                    reports={reports}
                    onAction={onAction}
                    sortingType={sortingType}
                    setSortingType={setSortingType}
                    onTableRowClick={strategySummaryRowClickHandler}
                    activeRowId={selectedStrategyRowId}
                    tableBoxClassName="max-h-[500px] pb-1"
                  />
                )}
                withSort
                tableWrapperClassname="!mx-0"
              />
              {strategyPortfolioSearchParams ? (
                <div className="mt-3 w-full">
                  <div className="text-sm font-bold mr-6 mb-2">
                    پرتفوی کانورژن: {selectedStrategyBaseAsset ?? "-"}
                  </div>
                  <TableTemplate<
                    IStrategyPortfolioSearchParams,
                    IConversionPortfolioTableReport
                  >
                    getDataTable={getConversionPortfolioTable}
                    searchParameters={strategyPortfolioSearchParams}
                    TableUI={(props) => (
                      <GroupTableUI
                        {...props}
                        tableBoxClassName="pb-1"
                        groupHeaderStyle={getBasketGroupHeaderStyle}
                      />
                    )}
                    withSort
                    tableWrapperClassname="!mx-0"
                  />
                </div>
              ) : null}
            </>
          ) : strategyTableType === "nakedOption" && nakedOptionSearchParams ? (
            <TableTemplate<
              INakedOptionPortfolioSearchParams,
              INakedOptionPortfolioTableReport
            >
              key={`naked-option-${nakedOptionSearchParams.assetType ?? "none"}`}
              getDataTable={getNakedOptionPortfolioTable}
              searchParameters={nakedOptionSearchParams}
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <GroupTableUI
                  reports={reports}
                  onAction={onAction}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  tableBoxClassName="max-h-[500px] pb-1"
                  groupTitleHeaderName="InstrumentName"
                />
              )}
              withSort
              tableWrapperClassname="!mx-0"
            />
          ) : segregationSearchParams ? (
            <>
              <TableTemplate<
                IPortfoSegregationSearchParams,
                IPortfoSegregationTableReport
              >
                getDataTable={getPortfoSegregationTable}
                searchParameters={segregationSearchParams}
                TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                  <CommonTableUI
                    reports={reports}
                    onAction={onAction}
                    sortingType={sortingType}
                    setSortingType={setSortingType}
                    onTableRowClick={segregationRowClickHandler}
                    activeRowId={selectedSegregationRow?.id}
                    tableBoxClassName="max-h-[500px] pb-1"
                    ref={prevSegregationRowRef}
                  />
                )}
                withSort
                tableWrapperClassname="!mx-0"
              />

              {detailsSearchParams ? (
            <TableTemplate<
              IPortfoSegregationDetailsSearchParams,
              IPortfoSegregationDetailsTableReport
            >
              getDataTable={
                detailsSearchParams.assetType === AssetType.DeltaHedge
                  ? getDeltaHedgeSegregationDetailsTable
                  : getPortfoSegregationDetailsTable
              }
              searchParameters={detailsSearchParams}
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <CommonTableUI
                  reports={reports}
                  onAction={onAction}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  tableBoxClassName="max-h-[500px] pb-1"
                />
              )}
              withSort
              tableWrapperClassname="!mx-0"
            />
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetails;
